import React, { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";

import AuthCardFooter from "../../components/auth/AuthCardFooter";
import styles from "./AuthPage.module.css";

import { requestPasswordResetCode } from "../../services/auth/passwordReset";
import { handleApiError } from "../../utils/handleApiError";

const schema = z.object({
  email: z
    .string()
    .min(1, "Email is required")
    .email("Enter a valid email address")
    .trim(),
});

const RecoverPassword = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const submittingRef = useRef(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { email: "" },
  });

  const onSubmit = async (data) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setLoading(true);
    try {
      await requestPasswordResetCode(data.email);
      // Security-friendly: we route forward regardless of whether the
      // account exists; the backend responds the same either way.
      toast.success("If that email is registered, a verification code is on its way.");
      navigate("/verify-otp", { state: { email: data.email } });
    } catch (error) {
      handleApiError(error);
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  return (
    <>
      <h1 className={styles.heading}>Reset your password</h1>
      <p className={styles.subheading}>
        Enter the email linked to your TOCA account and we'll send you a
        verification code.
      </p>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className={styles.form}
        noValidate
      >
        <div className={styles.field}>
          <label htmlFor="recover-id" className={styles.label}>
            Email
          </label>
          <div className={styles.inputWrap}>
            <FontAwesomeIcon icon={faEnvelope} className={styles.inputIcon} />
            <input
              id="recover-id"
              type="email"
              autoComplete="username"
              placeholder="you@clinic.org"
              className={styles.input}
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? "recover-id-error" : undefined}
              {...register("email")}
            />
          </div>
          {errors.email && (
            <span
              id="recover-id-error"
              role="alert"
              className={styles.errorMsg}
            >
              {errors.email.message}
            </span>
          )}
        </div>

        <button
          type="submit"
          className={styles.primaryBtn}
          disabled={loading}
          aria-busy={loading}
        >
          {loading ? "Sending..." : "Send verification code"}
        </button>

        <Link to="/login" className={styles.secondaryBtn}>
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Sign in
        </Link>
      </form>

      <p className={styles.requestAccess}>
        Don't have an account?{" "}
        <a
          href="mailto:sales@tocahealth.com?subject=Access%20request"
          className={styles.requestLink}
        >
          Request access
        </a>
      </p>

      <AuthCardFooter />
    </>
  );
};

export default RecoverPassword;
